import React, { FC, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

import { KeyboardKey } from '~/core/enums';
import { Ratio, Size } from '~/core/forge/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { createForgeImageURL, getEnvironmentAwareHref } from '~/core/utils';
import VideoPlayer from '~/views/components/videoPlayer/VideoPlayer';

import { useGameHighlightsQuery } from '../../api';
import { LinkedButton, SectionHeader } from '../../styles';
import { LandingGame } from '../../types';

import {
    GameHighlights,
    Highlight,
    HighlightDescription,
    HighlightsContainer,
    ThumbnailContainer,
    ThumbnailImage,
} from './styles';

type Props = {
    game: LandingGame;
};

const Highlights: FC<Props> = ({ game }) => {
    const isWebview = useIsWebview();

    const [videoId, setVideoId] = useState<string>();

    const { data: highlights, isLoading: areHighlightsLoading } = useGameHighlightsQuery(game.id);

    const highlightsWithoutRecap = highlights.items?.filter(
        (highlight) => !highlight?.tags?.some((tag) => tag.slug.includes('game-recap'))
    );

    const onPressEnter = (e, highlightId) => {
        if (e.code === KeyboardKey.ENTER) {
            e.preventDefault();
            setVideoId(highlightId);
        }
    };

    // TODO: Eventually show more than just first 3 highlights
    return !areHighlightsLoading && highlights.items.length > 0 ? (
        <GameHighlights>
            <SectionHeader aria-level={2}>
                <FormattedMessage description="Header describing the game highlights" defaultMessage="Highlights" />
            </SectionHeader>
            <HighlightsContainer>
                {highlightsWithoutRecap.slice(0, 3).map((highlight, index) => (
                    <Highlight
                        key={index}
                        onClick={() => setVideoId(highlight.fields.brightcoveId)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => onPressEnter(e, highlight.fields.brightcoveId)}
                    >
                        <ThumbnailContainer>
                            <ThumbnailImage
                                src={createForgeImageURL(
                                    highlight.thumbnail?.templateUrl,
                                    Ratio.LANDSCAPE_16_TO_9,
                                    Size.SIZE_40
                                )}
                            />
                            <PlayCircleOutlineIcon />
                        </ThumbnailContainer>
                        {highlight.fields.headline}
                        <HighlightDescription>{highlight.fields.duration}</HighlightDescription>
                    </Highlight>
                ))}
            </HighlightsContainer>
            {highlights.items.length > 3 && (
                <LinkedButton
                    type="link"
                    href={getEnvironmentAwareHref(`/video/game/gameid-${game.id}`, isWebview)}
                    tabIndex={0}
                >
                    <FormattedMessage
                        description="A phrase describing there is full coverage for the game."
                        defaultMessage="View more"
                    />
                </LinkedButton>
            )}
            {videoId && <VideoPlayer videoId={videoId} closePlayer={() => setVideoId(undefined)} />}
        </GameHighlights>
    ) : null;
};

export default Highlights;
